import { storyblokEditable } from "@storyblok/react";
import Image from "next/image";
import { sbBlok } from "@/types/sb-types";
import bgArrows from "../../public/bg_graphics/arrows_in.svg";
import {
  NODE_PARAGRAPH,
  NODE_UL,
  NODE_OL,
  NODE_LI,
  NODE_HEADING,
  render,
} from "storyblok-rich-text-react-renderer";
import Cta from "./Cta";
import clsx from "clsx";

interface TextWithMediaProps {
  blok: sbBlok;
}

const TextWithMedia = ({ blok }: TextWithMediaProps) => {
  const {
    image,
    image_mobile,
    content_text,
    layout_reverse,
    call_to_action,
    show_bg_graphics,
    bg_reverse,
    above_fold,
  } = blok;
  const cta = call_to_action[0] || null;

  return (
    <section {...storyblokEditable(blok)} className="grid">
      {show_bg_graphics && (
        <div
          className={clsx(
            "hidden pointer-events-none lg:grid-container lg:w-full lg:row-start-1 lg:col-start-1",
            {
              "pt-7 lg:pt-14":
                !!blok.spacing_top?.length &&
                blok.spacing_top[0].option === "large",
              "pt-14 lg:pt-[120px]":
                !!blok.spacing_top?.length &&
                blok.spacing_top[0].option === "x_large",
              "pt-[120px] lg:pt-[180px]":
                !!blok.spacing_top?.length &&
                blok.spacing_top[0].option === "xx_large",
              "pb-7 lg:pb-14":
                !!blok.spacing_bottom?.length &&
                blok.spacing_bottom[0].option === "large",
              "pb-14 lg:pb-[120px]":
                !!blok.spacing_bottom?.length &&
                blok.spacing_bottom[0].option === "x_large",
              "pb-[120px] lg:pb-[180px]":
                !!blok.spacing_bottom?.length &&
                blok.spacing_bottom[0].option === "xx_large",
            },
          )}
        >
          <div
            className={clsx("relative", {
              "col-start-1 col-span-4 lg:col-start-1 lg:col-span-8 2xl:col-start-1 2xl:col-span-16":
                bg_reverse,
              "col-start-1 col-span-4 lg:col-start-8 lg:col-span-8 2xl:col-start-14 2xl:col-span-16":
                !bg_reverse,
            })}
          >
            <div className="relative pt-[100%]">
              <Image
                src={bgArrows}
                alt="background arrows"
                fill
                sizes="100vw"
                className="object-contain"
                priority={above_fold}
              />
            </div>
          </div>
        </div>
      )}
      <div
        className={clsx("grid-container row-start-1 col-start-1", {
          "pt-7 lg:pt-14":
            !!blok.spacing_top?.length &&
            blok.spacing_top[0].option === "large",
          "pt-14 lg:pt-[120px]":
            !!blok.spacing_top?.length &&
            blok.spacing_top[0].option === "x_large",
          "pt-[120px] lg:pt-[180px]":
            !!blok.spacing_top?.length &&
            blok.spacing_top[0].option === "xx_large",
          "pb-7 lg:pb-14":
            !!blok.spacing_bottom?.length &&
            blok.spacing_bottom[0].option === "large",
          "pb-14 lg:pb-[120px]":
            !!blok.spacing_bottom?.length &&
            blok.spacing_bottom[0].option === "x_large",
          "pb-[120px] lg:pb-[180px]":
            !!blok.spacing_bottom?.length &&
            blok.spacing_bottom[0].option === "xx_large",
        })}
      >
        <div
          className={clsx(
            `relative overflow-hidden
            md:aspect-square 2xl:aspect-auto
            md:mx-auto lg:m-0
            h-[350px] md:w-[540px] md:h-[500px] lg:w-auto lg:h-auto
            col-start-1 col-span-4 lg:col-start-2 lg:col-span-8 lg:row-start-1 2xl:col-start-4 2xl:col-span-14 2xl:h-[800px]
            `,
            {
              "lg:!col-start-8 lg:!col-span-8 2xl:!col-start-16 2xl:!col-span-14":
                layout_reverse,
            },
          )}
        >
          {image_mobile?.filename && (
            <Image
              src={image_mobile.filename}
              alt={image_mobile.alt}
              className="block min-[701px]:hidden object-cover rounded-[10px]"
              priority={above_fold}
              sizes="(max-width: 960px) 100vw"
              fill
            />
          )}
          <Image
            src={image.filename}
            alt={image.alt}
            className={clsx("object-cover rounded-[10px]", {
              "hidden min-[701px]:block": image_mobile?.filename,
              block: !image_mobile?.filename,
            })}
            priority={above_fold}
            sizes="(max-width: 960px) 100vw, 60vw"
            quality={100}
            fill
          />
        </div>
        <div
          className={clsx(
            `flex flex-col justify-center items-center lg:items-start text-center lg:text-left
            mt-10 md:mt-8 lg:mt-0
            col-start-1 col-span-4 lg:col-start-11 lg:col-span-5 lg:row-start-1 2xl:col-start-19 2xl:col-span-11
          `,
            {
              "lg:!col-start-2 lg:!col-span-5 2xl:!col-start-4 2xl:!col-span-11":
                layout_reverse,
            },
          )}
        >
          {content_text &&
            render(content_text, {
              nodeResolvers: {
                [NODE_PARAGRAPH]: (children) => (
                  <p className="mb-5 text-base lg:text-xl">{children}</p>
                ),
                [NODE_UL]: (children) => (
                  <ul className="pl-16 list-disc m-0 p-0 xl:w-1/2 text-base lg:text-xl">
                    {children}
                  </ul>
                ),
                [NODE_OL]: (children) => (
                  <ol className="pl-16 list-decimal text-base lg:text-xl">
                    {children}
                  </ol>
                ),
                [NODE_LI]: (children) => (
                  <li className="mb-6 last:mb-0 flex gap-5 [&>*]:block [&>*]:w-full">
                    {children}
                  </li>
                ),
                [NODE_HEADING]: (
                  children,
                  props: { level: 1 | 2 | 3 | 4 | 5 | 6 },
                ) => {
                  if (props?.level === 1)
                    return <h1 className="text-2xl lg:text-4xl">{children}</h1>;

                  if (props?.level === 2)
                    return (
                      <h2 className="text-2xl lg:text-4xl m-0">{children}</h2>
                    );

                  return (
                    <span className="text-2xl lg:text-4xl">{children}</span>
                  );
                },
              },
            })}
          {cta && (
            <Cta
              href={cta.link.url}
              fullSlug={cta.leaderboard_link || cta.link?.story?.full_slug}
              name={cta.name}
              className="mt-1.5"
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default TextWithMedia;
